import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Circle, Download,
  FileText, QrCode, Mail, Loader2, Shield, MapPin, Trophy,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * /lootmaster/portal — gated Loot Master onboarding hub.
 *
 * Backend route: /api/lootmaster/portal/me returns settings + auto-checks +
 * the user's manual-toggle state. Three of the five checklist items are
 * computed (profile, first_drop, first_win); the other two (sponsor pitched,
 * community joined) are user-attested and toggled via POST
 * /api/lootmaster/portal/checklist.
 *
 * Two PDF artifacts are linked from this page:
 *   /api/lootmaster/portal/pitch-deck.pdf   (sponsor outreach deck)
 *   /api/lootmaster/portal/card-template.pdf (printable 4×6 QR poster)
 *
 * Access: backend enforces role ∈ {lootmaster, admin}. Anyone else gets 403.
 * Frontend shows a clean "Not authorized" panel and a CTA back to /lootmaster.
 */
export default function LootMasterPortalPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await api.get("/lootmaster/portal/me");
      setData(res.data);
      setError(null);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        navigate("/login?next=/lootmaster/portal", { replace: true });
        return;
      }
      const detail = err?.response?.data?.detail || "Couldn't load the portal.";
      setError(typeof detail === "string" ? detail : "Couldn't load the portal.");
    }
  }, [navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login?next=/lootmaster/portal", { replace: true });
      return;
    }
    load();
  }, [authLoading, user, navigate, load]);

  const tick = async (key, completed) => {
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      await api.post("/lootmaster/portal/checklist", { key, completed });
      await load();
      toast.success(completed ? "Marked complete" : "Marked incomplete");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't update.");
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };

  if (authLoading || (!data && !error)) {
    return (
      <div className="splash-root flex items-center justify-center" data-testid="lm-portal-loading">
        <div className="splash-mono text-sm opacity-60">Loading portal…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="splash-root flex items-center justify-center px-4" data-testid="lm-portal-error">
        <div className="max-w-md w-full bg-[#0d100d] border-2 border-[#d7a62a]/40 p-8 text-center">
          <Shield size={36} className="mx-auto text-[#d7a62a]" />
          <h1 className="splash-display text-3xl uppercase mt-4">Portal locked</h1>
          <p className="mt-3 text-[14px] text-[#f7f3e6]/70 leading-relaxed">{error}</p>
          <Link to="/lootmaster" className="btn-loot-solid mt-6" data-testid="lm-portal-error-cta">
            Apply to be a Loot Master <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    );
  }

  const s = data.settings || {};
  const completion = data.completion || {};
  const checklistItems = Array.isArray(s.checklist) ? s.checklist : [];

  return (
    <div className="splash-root" data-testid="lm-portal-page">
      <PortalHero s={s} data={data} />
      <ChecklistSection
        items={checklistItems}
        completion={completion}
        onTick={tick}
        busy={busy}
      />
      <ResourcesSection s={s} />
      <StatsSection data={data} />
      <PortalFooter s={s} />
    </div>
  );
}

function PortalHero({ s, data }) {
  return (
    <section className="splash-hero relative" data-testid="lm-portal-hero">
      <div className="splash-hero-overlay" aria-hidden="true" />
      <div className="splash-grain" aria-hidden="true" />
      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 pt-10 md:pt-16 pb-12 md:pb-16">
        <Link
          to="/home"
          className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#76B82A] inline-flex items-center gap-2 hover:text-[#84d130]"
          data-testid="lm-portal-back"
        >
          <ArrowLeft size={12} /> Back to dashboard
        </Link>
        <div className="mt-8 splash-mono uppercase tracking-[0.22em] text-[11px] text-[#d7a62a]">
          {s.welcome_overline || "Welcome, Loot Master"}
        </div>
        <h1 className="splash-display text-4xl md:text-6xl uppercase mt-3 leading-[0.95]">
          <span className="block headline-stamp">
            {(s.welcome_headline || "Your territory.").split(".")[0]}.
          </span>
          <span className="block headline-stamp--green mt-1 md:mt-2">
            {(s.welcome_headline || "").split(".").slice(1).join(".").trim() || "Your hunt."}
          </span>
        </h1>
        <p className="mt-5 max-w-2xl text-[15px] md:text-base text-[#f7f3e6]/80 leading-relaxed">
          {s.welcome_body}
        </p>
        {data.city && (
          <div className="mt-6 inline-flex items-center gap-2 splash-mono uppercase tracking-widest text-[11px] text-[#76B82A]">
            <MapPin size={12} /> Territory: {data.city}
          </div>
        )}
      </div>
    </section>
  );
}

function ChecklistSection({ items, completion, onTick, busy }) {
  if (items.length === 0) return null;
  const computedKeys = new Set(["profile", "first_drop", "first_win"]);

  return (
    <section className="cream-section py-14 md:py-20" data-testid="lm-portal-checklist">
      <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-8">
        <h2 className="splash-display text-3xl md:text-5xl uppercase">
          Your launch checklist.
        </h2>
        <p className="mt-3 text-[14px] text-[#0d100d]/70">
          The first three update automatically. The last two — tap when done.
        </p>
        <ol className="mt-8 space-y-3">
          {items.map((it, i) => {
            const done = !!completion[it.key];
            const isComputed = computedKeys.has(it.key);
            return (
              <li
                key={it.key || i}
                className={`bg-[#fffaef] border-2 p-4 md:p-5 flex items-start gap-4 ${done ? "border-[#76B82A]" : "border-[#0d100d]"}`}
                data-testid={`lm-portal-step-${it.key}`}
              >
                <button
                  type="button"
                  disabled={isComputed || busy[it.key]}
                  onClick={() => onTick(it.key, !done)}
                  className={`shrink-0 mt-0.5 ${isComputed ? "cursor-not-allowed" : "cursor-pointer"}`}
                  data-testid={`lm-portal-tick-${it.key}`}
                  aria-label={done ? "Mark incomplete" : "Mark complete"}
                >
                  {busy[it.key] ? (
                    <Loader2 size={26} className="animate-spin text-[#76B82A]" />
                  ) : done ? (
                    <CheckCircle2 size={26} className="text-[#76B82A]" />
                  ) : (
                    <Circle size={26} className="text-[#0d100d]/30" />
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="splash-display uppercase text-lg md:text-xl leading-tight">
                    {it.title}
                  </div>
                  <div className="mt-1 text-[13px] md:text-[14px] text-[#0d100d]/75 leading-snug">
                    {it.body}
                  </div>
                  {isComputed && (
                    <div className="mt-1 splash-mono text-[10px] uppercase tracking-widest text-[#0d100d]/45">
                      Auto-tracked
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

function ResourcesSection({ s }) {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const resources = [
    {
      icon: FileText,
      title: s.pitch_deck_title || "Sponsor Pitch Deck",
      body: "PDF you can email to local businesses. We pre-wrote the case for you.",
      href: `${backend}/api/lootmaster/portal/pitch-deck.pdf`,
      cta: "Download deck",
      testid: "pitch-deck",
    },
    {
      icon: QrCode,
      title: s.card_template_title || "4×6 QR Poster Template",
      body: "Printable 4×6 poster with QR placeholder. Tape it up at sponsor storefronts.",
      href: `${backend}/api/lootmaster/portal/card-template.pdf`,
      cta: "Download poster",
      testid: "qr-poster",
    },
  ];
  return (
    <section className="bg-[#0d100d] border-t border-[#76B82A]/15 py-14 md:py-20" data-testid="lm-portal-resources">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        <h2 className="splash-display text-3xl md:text-5xl uppercase">
          Toolkit.
        </h2>
        <p className="mt-3 text-[14px] text-[#f7f3e6]/70 max-w-xl">
          Everything you need to land your first sponsor and post your first drop.
        </p>
        <div className="mt-8 grid md:grid-cols-2 gap-5">
          {resources.map((r, i) => {
            const I = r.icon;
            return (
              <article key={i} className="private-card" data-testid={`lm-portal-resource-${r.testid}`}>
                <span className="pc-icon"><I size={20} /></span>
                <h3 className="splash-display text-xl md:text-2xl uppercase mt-3">{r.title}</h3>
                <p className="mt-2 text-[14px] text-[#f7f3e6]/75 leading-relaxed">{r.body}</p>
                <a
                  href={r.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-loot-solid mt-4 inline-flex"
                  data-testid={`lm-portal-resource-cta-${r.testid}`}
                >
                  <Download size={14} /> {r.cta}
                </a>
              </article>
            );
          })}
        </div>
        {s.support_email && (
          <div className="mt-10 inline-flex items-center gap-2 splash-mono uppercase tracking-widest text-[11px] text-[#76B82A]">
            <Mail size={14} /> Stuck? Email {s.support_email}
          </div>
        )}
      </div>
    </section>
  );
}

function StatsSection({ data }) {
  const items = [
    { label: "Drops created", value: data.drop_count || 0, icon: MapPin },
    { label: "Winners crowned", value: data.win_count || 0, icon: Trophy },
  ];
  return (
    <section className="cream-section py-14 md:py-20" data-testid="lm-portal-stats">
      <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-8">
        <h2 className="splash-display text-3xl md:text-5xl uppercase">
          Your numbers.
        </h2>
        <div className="mt-8 grid grid-cols-2 gap-4">
          {items.map((it, i) => {
            const I = it.icon;
            return (
              <div
                key={i}
                className="bg-[#fffaef] border-2 border-[#0d100d] p-5 shadow-[6px_6px_0_#0d100d]"
                data-testid={`lm-portal-stat-${i}`}
              >
                <div className="flex items-center gap-2 splash-mono uppercase tracking-widest text-[10px] text-[#4f8c19]">
                  <I size={12} /> {it.label}
                </div>
                <div className="mt-2 splash-display text-4xl md:text-5xl">{it.value}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function PortalFooter({ s }) {
  return (
    <footer className="splash-footer py-8 text-center" data-testid="lm-portal-footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8 splash-mono text-[12px] text-[#f7f3e6]/55">
        © The Local Loot · Loot Master Portal · {s.support_email || "hi@thelocalloot.com"}
      </div>
    </footer>
  );
}
